import { createTheme, ThemeOptions } from '@mui/material'
import { KeyboardArrowDownIcon } from 'src/assets/icons'
import { THEMES, themesOptions } from './palette'

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primary: true
    secondary: true
    disabled: true
    appearDisabled: true
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    medium_18: true
    small_14: true
  }
}

declare module '@mui/material/Drawer' {
  interface DrawerPropsVariantOverrides {
    menu: true
  }
}

const theme = createTheme(themesOptions[THEMES.LIGHT])

export const themeVariants: ThemeOptions = {
  components: {
    MuiOutlinedInput: { styleOverrides: { notchedOutline: { borderColor: '#E4E7EB', borderRadius: '8px', color: '#344054' } } },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: '18px',
          '&::placeholder': {
            color: '#344054',
            opacity: 1
          },
          '&::-webkit-input-placeholder': {
            color: '#344054',
            opacity: 1
          },
          '&::-moz-placeholder': {
            color: '#344054',
            opacity: 1
          },
          '&::-ms-input-placeholder': {
            color: '#344054',
            opacity: 1
          },
          '&:-moz-placeholder': {
            color: '#344054',
            opacity: 1
          }
        }
      }
    },
    MuiButton: {
      variants: [
        {
          props: {
            variant: 'primary'
          },
          style: {
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            border: `1px solid ${theme.palette.primary.main}`,
            ':hover': {
              backgroundColor: theme.palette.primary.main,
              opacity: '0.7'
            }
          }
        },
        {
          props: {
            variant: 'secondary'
          },
          style: {
            backgroundColor: 'white',
            color: theme.palette.primary.main,
            border: `1px solid #C7CED4`
          }
        },
        {
          props: {
            variant: 'appearDisabled'
          },
          style: {
            backgroundColor: '#A6B1BB',
            color: 'white',
            borderRadius: '8px',
            border: '1px solid #A6B1BB',
            cursor: 'not-allowed',
            ':hover': {
              backgroundColor: '#A6B1BB'
            }
          }
        }
      ],
      styleOverrides: {
        root: {
          padding: '16px 20px 16px 20px',
          letterSpacing: '0.15',
          borderRadius: '8px',
          fontWeight: 500,
          boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
          lineHeight: '1.2',
          fontSize: '16px',
          textTransform: 'capitalize',
          '&:disabled': {
            backgroundColor: '#A6B1BB',
            color: 'white',
            border: `1px solid #A6B1BB`
          }
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          '& .MuiDrawer-paper': {
            '@media(max-width: 1200px)': {
              overflowY: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': {
                display: 'none'
              }
            },
            '@media(max-width: 700px)': {
              overflowY: 'auto'
            }
          }
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: 'Roboto, sans-serif',
          color: theme.palette.text.primary
        }
      },
      variants: [
        {
          props: { variant: 'medium_18' },
          style: {
            fontSize: '18px',
            lineHeight: '21px'
          }
        },
        {
          props: { variant: 'small_14' },
          style: {
            fontSize: '14px',
            lineHeight: '16px'
          }
        }
      ]
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          display: 'none'
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'capitalize',
          fontSize: '24px',
          border: '1px solid #98A2B3',
          borderRadius: '8px',
          '&.Mui-selected': {
            color: 'white',
            backgroundColor: theme.palette.primary.dark,
            fontWeight: 500
          }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        popper: {
          backgroundColor: theme.palette.primary.main,
          borderRadius: '8px',
          padding: '16px'
        },
        tooltip: {
          backgroundColor: theme.palette.primary.main,
          maxWidth: '434px',
          fontSize: '18px',
          fontWeight: 500,
          padding: 0,
          marginTop: '0px !important',
          marginBottom: '0px !important'
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        container: {
          backgroundColor: '#022C41B2'
        }
      }
    },

    MuiSelect: {
      defaultProps: {
        IconComponent: KeyboardArrowDownIcon
      },
      styleOverrides: {
        outlined: {
          borderRadius: '8px',
          border: '2px solid #E4E7EB',
          boxShadow: '0px 1px 2px rgb(16 24 40 / 5%)',
          paddingTop: '15px',
          paddingBottom: '15px'
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ ownerState }) => {
          if (ownerState.variant === 'outlined') {
            return {
              '.MuiOutlinedInput-root': {
                borderRadius: '8px',
                boxShadow: '0px 1px 2px rgb(16 24 40 / 5%)'
              }
            }
          }
        }
      }
    },
    MuiModal: {
      styleOverrides: {
        root: ({ ownerState }) => {
          if (!ownerState.className?.includes('MuiMenu-root')) {
            return {
              backgroundColor: '#022C41B2'
            }
          }
        }
      }
    }
  }
}
